export type DecorationFormat = 'glb' | 'gltf' | 'obj' | 'fbx' | 'stl' | 'ply' | 'dae' | '3ds';

export interface StoredDecorationAsset {
    id: string;
    name: string;
    fileName: string;
    format?: DecorationFormat;
    data?: ArrayBuffer;
    sourceFileName?: string;
    sourceFormat?: 'max' | 'fbx';
    serverVersion?: string;
    sourceData?: ArrayBuffer | Blob;
    updatedAt: string;
}

export class DecorationStorage {
    private readonly databaseName = 'quiz-rush-3d-decorations';
    private readonly storeName = 'assets';
    private readonly version = 1;

    public async list(): Promise<StoredDecorationAsset[]> {
        const database = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(this.storeName, 'readonly');
            const request = transaction.objectStore(this.storeName).getAll();
            request.onsuccess = () => resolve((request.result as StoredDecorationAsset[]) || []);
            request.onerror = () => reject(request.error || new Error('장식 에셋을 불러오지 못했습니다.'));
            transaction.oncomplete = () => database.close();
        });
    }

    public async put(record: StoredDecorationAsset): Promise<void> {
        const database = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(this.storeName, 'readwrite');
            transaction.objectStore(this.storeName).put(record);
            transaction.oncomplete = () => {
                database.close();
                resolve();
            };
            transaction.onerror = () => {
                database.close();
                reject(transaction.error || new Error('장식 에셋을 저장하지 못했습니다.'));
            };
        });
    }

    public async delete(id: string): Promise<void> {
        const database = await this.open();
        return new Promise((resolve, reject) => {
            const transaction = database.transaction(this.storeName, 'readwrite');
            transaction.objectStore(this.storeName).delete(id);
            transaction.oncomplete = () => {
                database.close();
                resolve();
            };
            transaction.onerror = () => {
                database.close();
                reject(transaction.error || new Error('장식 에셋을 삭제하지 못했습니다.'));
            };
        });
    }

    private open(): Promise<IDBDatabase> {
        if (typeof indexedDB === 'undefined') {
            return Promise.reject(new Error('이 브라우저는 장식 에셋 저장을 지원하지 않습니다.'));
        }
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.databaseName, this.version);
            request.onupgradeneeded = () => {
                const database = request.result;
                if (!database.objectStoreNames.contains(this.storeName)) {
                    database.createObjectStore(this.storeName, { keyPath: 'id' });
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error || new Error('장식 에셋 저장소를 열지 못했습니다.'));
        });
    }
}
